"use client";

import { useRef, useState } from "react";
import {
  recordRemedyOutcome,
  type AccountRemedyFollowup,
  type RemedyOutcomeCode,
  type RemedyOutcomeResponse,
} from "./account-api";

export const REMEDY_OUTCOME_OPTIONS: ReadonlyArray<{
  code: RemedyOutcomeCode;
  label: string;
}> = [
  { code: "helped", label: "It helped" },
  { code: "partly_helped", label: "It helped a little" },
  { code: "no_change", label: "I have not noticed a change" },
  { code: "not_tried", label: "I have not tried it yet" },
];

interface PendingAttempt {
  signature: string;
  idempotencyKey: string;
}

export function AccountRemedyOutcome({
  followups,
  token,
  onRecorded,
}: {
  followups: AccountRemedyFollowup[];
  token: string;
  onRecorded?: (outcome: RemedyOutcomeResponse) => void;
}) {
  const followup = followups.find((item) => item.outcome_code === null);
  const [selected, setSelected] = useState<RemedyOutcomeCode | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState<RemedyOutcomeResponse | null>(null);
  const pendingAttempt = useRef<PendingAttempt | null>(null);

  if (!followup && !saved) return null;
  if (saved) return <SavedOutcome outcome={saved} />;

  async function saveOutcome() {
    if (!followup || !selected || saving) return;
    const signature = `${followup.prescription_id}:${selected}`;
    if (pendingAttempt.current?.signature !== signature) {
      pendingAttempt.current = {
        signature,
        idempotencyKey: crypto.randomUUID(),
      };
    }
    setSaving(true);
    setError("");
    try {
      const outcome = await recordRemedyOutcome(
        token,
        followup.prescription_id,
        selected,
        pendingAttempt.current.idempotencyKey
      );
      setSaved(outcome);
      onRecorded?.(outcome);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save your upay result.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      aria-labelledby="upay-outcome-heading"
      className="mb-6 rounded-2xl border border-[var(--color-accent-border)] bg-[var(--color-surface-warm)] p-5 shadow-[var(--shadow-elevation-low)] sm:p-6"
    >
      <p className="text-eyebrow font-semibold uppercase tracking-eyebrow text-[var(--color-accent)]">
        Your upay follow-up
      </p>
      <h2 id="upay-outcome-heading" className="mt-2 font-serif text-2xl">
        Did this upay make a difference?
      </h2>
      <p className="mt-2 text-sm leading-6 text-[var(--color-text-sub)]">
        Tell your Jyotishi what happened after this upay. Your answer is saved separately from the
        message that brought you back.
      </p>
      <blockquote className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-4 text-sm leading-6 text-[var(--color-text-main)]">
        {followup?.remedy_text}
      </blockquote>

      <fieldset className="mt-5">
        <legend className="text-sm font-semibold text-[var(--color-text-main)]">
          What did you notice?
        </legend>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {REMEDY_OUTCOME_OPTIONS.map((option) => (
            <label
              key={option.code}
              className={`flex min-h-11 cursor-pointer items-center rounded-xl border px-4 py-3 text-sm font-medium ${
                selected === option.code
                  ? "border-[var(--color-accent)] bg-[var(--color-accent-light)] text-[var(--color-text-main)]"
                  : "border-[var(--color-border)] bg-[var(--color-surface-raised)] text-[var(--color-text-sub)]"
              }`}
            >
              <input
                type="radio"
                name={`remedy-outcome-${followup?.prescription_id}`}
                value={option.code}
                checked={selected === option.code}
                onChange={() => setSelected(option.code)}
                className="mr-3 accent-[var(--color-accent)]"
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>

      {error ? (
        <p role="alert" className="mt-3 text-sm text-[var(--color-error-text)]">
          {error}
        </p>
      ) : null}
      <button
        type="button"
        disabled={!selected || saving}
        onClick={() => void saveOutcome()}
        className="mt-5 inline-flex min-h-11 items-center justify-center rounded-xl border border-[var(--color-accent)] bg-[var(--color-surface-raised)] px-5 py-3 text-sm font-semibold text-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {saving ? "Saving..." : "Save what happened"}
      </button>
    </section>
  );
}

function SavedOutcome({ outcome }: { outcome: RemedyOutcomeResponse }) {
  const label =
    REMEDY_OUTCOME_OPTIONS.find((option) => option.code === outcome.outcome_code)?.label ??
    "Outcome saved";
  return (
    <section
      aria-live="polite"
      className="mb-6 rounded-2xl border border-[var(--color-accent-border)] bg-[var(--color-surface-warm)] p-5 shadow-[var(--shadow-elevation-low)] sm:p-6"
    >
      <p className="text-eyebrow font-semibold uppercase tracking-eyebrow text-[var(--color-accent)]">
        Upay result saved
      </p>
      <h2 className="mt-2 font-serif text-2xl">{label}</h2>
      <p className="mt-2 text-sm leading-6 text-[var(--color-text-sub)]">
        Noted. This result is now saved with your reading.
      </p>
    </section>
  );
}
